import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { CSpinner, CTooltip } from '@coreui/react-pro'
import { CFormTextareaProps } from '@coreui/react-pro/dist/components/form/CFormTextarea'
import { mdiArrowRight, mdiClose, mdiFileOutline, mdiPaperclip } from '@mdi/js'
import Icon from '@mdi/react'
import html2canvas from 'html2canvas'
import { omit } from 'lodash'
import mammoth from 'mammoth'
import * as pdfjsLib from 'pdfjs-dist/webpack'

import { black, middleBlue } from '@constants/colors'
import { useToast } from '@contexts/toastProvider'
import { IGeneratorInput } from '@interfaces/components/atoms/GeneratorInput'

import CFormTextareaAutoResize from '../CFormTextareaAutoResize'

import './styles.scss'

const GeneratorInput: React.FC<Omit<CFormTextareaProps, 'onSubmit'> & IGeneratorInput> = (
  props,
) => {
  const toast = useToast()

  const [isFocused, setIsFocused] = useState(false)
  const [attachments, setAttachments] = useState<string[]>()

  const [selectedFileName, setSelectedFileName] = useState('')
  const [isProcessingFile, setIsProcessingFile] = useState(false)

  const inputFile = useRef<HTMLInputElement>(null)

  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`
  }, [])

  const handleProcessPdf = async (pdfFile: File) => {
    if (pdfFile) {
      setSelectedFileName(pdfFile.name)
      setIsProcessingFile(true)

      try {
        // Load the PDF
        const loadingTask = pdfjsLib.getDocument(URL.createObjectURL(pdfFile))
        const pdf = await loadingTask.promise

        // Loop through all pages of the document
        const totalPages = pdf.numPages

        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber)
          const viewport = page.getViewport({ scale: 1.5 })
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          canvas.height = viewport.height
          canvas.width = viewport.width

          // Render the page on the canvas
          if (context) {
            await page.render({
              canvasContext: context,
              viewport: viewport,
            }).promise

            const image = canvas.toDataURL()
            if (pageNumber === 1) {
              setAttachments([image])
            } else {
              setAttachments((prev) => [...(prev || []), image])
            }
          }
        }
      } catch (error) {
        toast.show({
          message: 'An error happened while trying to read the PDF content',
          state: 'error',
        })
        console.error('Error processing PDF:', error)
        setSelectedFileName('')
        setAttachments(undefined)
      } finally {
        setIsProcessingFile(false)
      }
    }
  }

  const handleProcessImage = async (file: File) => {
    if (file) {
      setSelectedFileName(file.name)
      setIsProcessingFile(true)

      try {
        // Load the PDF
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64 = reader.result?.toString()
          if (base64) {
            setAttachments([base64])
          }
        }
        reader.readAsDataURL(file)
      } catch (error) {
        toast.show({
          message: 'An error happened while trying to read image content',
          state: 'error',
        })
        setSelectedFileName('')
        setAttachments(undefined)
      } finally {
        setIsProcessingFile(false)
      }
    }
  }

  const handleProcessDocX = async (file: File) => {
    if (file) {
      setSelectedFileName(file.name)
      setIsProcessingFile(true)

      try {
        // Extract the content as HTML from the .docx file
        const arrayBuffer = await file.arrayBuffer()
        const result = await mammoth.convertToHtml({ arrayBuffer })

        // Create an off-screen div and set the extracted HTML
        const offscreenElement = document.createElement('div')
        offscreenElement.style.position = 'absolute'
        offscreenElement.style.top = '-9999px'
        offscreenElement.style.left = '-9999px'
        offscreenElement.style.width = '800px' // Set desired width for rendering
        offscreenElement.innerHTML = result.value

        document.body.appendChild(offscreenElement)

        // Convert the off-screen content to an image using html2canvas
        const canvas = await html2canvas(offscreenElement, { scale: 1.5, logging: false })
        document.body.removeChild(offscreenElement) // Clean up the DOM

        const imgData = canvas.toDataURL('image/png')
        setAttachments([imgData])
      } catch (error) {
        toast.show({
          message: 'An error happened while trying to read the file content',
          state: 'error',
        })
        setSelectedFileName('')
        setAttachments(undefined)
      } finally {
        setIsProcessingFile(false)
      }
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0]

    if (file) {
      if (file.size > 10000000) {
        alert('The document size should be less than 10MB.')
      } else if (file.type === 'application/pdf') {
        handleProcessPdf(file)
      } else if (file.type.includes('image')) {
        handleProcessImage(file)
      } else if (file.name.endsWith('.docx')) {
        handleProcessDocX(file)
      } else {
        alert('This file type is not supported yet!')
      }
    }
  }

  const disabled = useMemo(
    () => (!selectedFileName && props.disabled) || isProcessingFile,
    [props.disabled, selectedFileName, isProcessingFile],
  )

  return (
    <div
      className={`GIContainer ${isFocused ? 'GIContainerFocused' : ''} ${props.containerClassName}`}
    >
      {props.withAttachment && !selectedFileName ? (
        <CTooltip
          content="Upload examples or old files you'd like to turn into interactive forms."
          offset={[0, 8]}
        >
          <div
            className="GIAttachmentButton"
            onClick={() => {
              inputFile.current?.click()
            }}
          >
            <Icon size={1} path={mdiPaperclip} color={black} />
            <input
              ref={inputFile}
              id="generatorAttachment"
              type="file"
              accept="image/*,.docx,application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </CTooltip>
      ) : null}
      <div style={{ flex: 1 }}>
        <CFormTextareaAutoResize
          style={{ borderWidth: 0, flex: 1, boxShadow: 'none' }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...omit(
            props,
            'onSubmit',
            'containerClassName',
            'isLoading',
            'disabled',
            'customIcon',
            'withAttachment',
            'sendButtonText',
          )}
        />
        {selectedFileName ? (
          <div className="GIFileContainer mb-2 ms-3 text-button">
            {isProcessingFile ? (
              <>
                <CSpinner size="sm" />
                <div className="mx-2">{selectedFileName}</div>
              </>
            ) : (
              <>
                <Icon path={mdiFileOutline} size={0.8} />
                <div className="mx-2">{selectedFileName}</div>
                <div
                  className="clickable"
                  onClick={() => {
                    setSelectedFileName('')
                    setAttachments(undefined)
                  }}
                >
                  <Icon path={mdiClose} size={0.8} color={black} />
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
      <div
        className="GISubmitButton"
        onClick={() => {
          if (!disabled) {
            props.onSubmit(attachments)
          }
        }}
        style={{
          backgroundColor: !disabled ? middleBlue : undefined,
        }}
      >
        {props.isLoading ? (
          <CSpinner size="sm" style={{ color: black }} />
        ) : (
          <div className="d-flex justify-content-center align-items-center">
            {props.sendButtonText ? (
              <div className="me-2 button-font">{props.sendButtonText}</div>
            ) : null}
            <Icon size={0.8} path={props.customIcon || mdiArrowRight} color={black} />
          </div>
        )}
      </div>
    </div>
  )
}

export default GeneratorInput
